import React, { useState, useEffect, useMemo } from "react";
import "../Dashboard/superManagerDashboard.css";
import { Layout, message, Spin } from "antd";
import { Accounts_Query, Dashboard_API, GET_ACCOUNT_SUMMARY, GET_EXPORT_ACCOUNT_SUMMARY, GET_LOCATION_SUMMARY, GET_USERS_ASSIGNED_TO_ACCOUNT, Role_Account_Query } from "../../services/userAPI";
import { useLazyQuery, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import SearchableDropdown from "../../reusableField/searchableDropdownField";
import { Map, List, ChevronRight, X, MapPin } from 'lucide-react';
import Clients from "../../Images/clientsLogo.png";
import LenseLogo from "../../Images/lense-logo-red-color.png";
import DartsLogo from "../../Images/darts-logo-red-color.png";
import ClanLogo from "../../Images/clan-logo-red-color.png";
import Lense from "../../Images/Lense-Logo.png";
import Darts from "../../Images/Darts-Logo.png";
import Clan from "../../Images/clan_logo.png";
import Roles from "../../Images/rolesLogo.png";
import Users from "../../Images/usersLogo.png";
import { Select } from 'antd';

// Leaflet Imports
import { MapContainer, TileLayer, CircleMarker, Tooltip, MapConsumer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const { Content } = Layout;

const AdminDashboard = () => {
    const [viewMode, setViewMode] = useState('table');
    const [breadcrumb, setBreadcrumb] = useState(['All Clients']);
    const [hoverIndex, setHoverIndex] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedAccountId, setSelectedAccountId] = useState(null);
    const [selectedJobroleId, setSelectedJobroleId] = useState(null);
    const [selectedRolesForDropdown, setSelectedRolesForDropdown] = useState([]);
    const [selectedZone, setSelectedZone] = useState(null);
    const [selectedStateId, setSelectedStateId] = useState(null);
    const [selectedCityId, setSelectedCityId] = useState(null);
    const [selectedBranchId, setSelectedBranchId] = useState(null); // optional
    const [fetchSummary, { data: summaryData, loading: summaryLoading }] = useLazyQuery(GET_LOCATION_SUMMARY, {
        fetchPolicy: "network-only",
    });

    const [fetchAccountSummary, { data: accountSummaryData, loading: accountSummaryLoading }] = useLazyQuery(GET_ACCOUNT_SUMMARY, {
        fetchPolicy: "network-only",
    });

    useEffect(() => {
        if (selectedAccountId) {
            fetchAccountSummary({
                variables: {
                    accountId: selectedAccountId,
                    jobroleId: selectedJobroleId || null,
                },
            });
        }
    }, [selectedAccountId, selectedJobroleId, fetchAccountSummary]);

    // Trigger once on mount — or whenever you want
    useEffect(() => {
        fetchSummary({
            variables: {
                accountId: null,   // ← intentionally null to get all accounts
                jobroleId: null,   // ← same here
                zone: null,
                stateId: null,
                cityId: null,
            },
        });
    }, [fetchSummary]);

    useEffect(() => {
        fetchSummary({
            variables: {
                accountId: selectedAccountId || null,
                jobroleId: selectedJobroleId || null,
                zone: selectedZone ?? null,
                stateId: selectedStateId ?? null,
                cityId: selectedCityId ?? null,
                branchId: selectedBranchId ?? null,
            },
        });
    }, [selectedAccountId, selectedJobroleId, selectedZone, selectedStateId, selectedCityId, selectedBranchId, fetchSummary]);

    // Auto-fit map to markers - fixes MapConsumer + useEffect inside callback
    const FitBoundsHook = ({ data }) => {
        const map = useMap();

        useEffect(() => {
            const validCoords = data
                .filter(loc => loc.latitute && loc.longitude && loc.name !== 'Unmapped')
                .map(loc => {
                    const lat = parseFloat(loc.latitute);
                    const lng = parseFloat(loc.longitude);
                    return isNaN(lat) || isNaN(lng) ? null : [lat, lng];
                })
                .filter(Boolean);

            if (validCoords.length > 0) {
                const bounds = L.latLngBounds(validCoords);
                map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
            } else {
                map.setView([20.5937, 78.9629], 5);
            }
        }, [data, map]);

        return null;
    };

    // Replace the static topMetrics with dynamic one
    const topMetrics = useMemo(() => {
        // If no client selected → show global mock stats
        if (!selectedAccountId) {
            return {
                activeToday: "",
                activeMTD: "",
                newThisMonth: "",
                adoptionRate: ""
            };
        }

        // Loading state
        if (accountSummaryLoading) {
            return {
                activeToday: "-",
                activeMTD: "-",
                newThisMonth: "-",
                adoptionRate: "-"
            };
        }

        // Extract the first (and only) account summary
        const accountSummary = accountSummaryData?.getAccountSummary?.data?.[0];

        if (!accountSummary) {
            return {
                activeToday: 0,
                activeMTD: 0,
                newThisMonth: 0,
                adoptionRate: "0"
            };
        }

        const adoptionRate = accountSummary.total_users > 0
            ? ((accountSummary.active_mtd / accountSummary.total_users) * 100).toFixed(1)
            : "0";

        return {
            activeToday: accountSummary.active_today || 0,
            activeMTD: accountSummary.active_mtd || 0,
            newThisMonth: accountSummary.new_this_month || 0,
            adoptionRate: adoptionRate + "%"
        };
    }, [selectedAccountId, selectedJobroleId, accountSummaryData, accountSummaryLoading]);

    const handleDrillDown = (location) => {
        const filterCount = selectedAccountId && selectedJobroleId ? 3 : selectedAccountId ? 2 : 1;
        const level = breadcrumb.length - filterCount; // 0=zone,1=state,2=city,3=branch

        if (!selectedZone) {
            setSelectedZone(location.name);
        } else if (!selectedStateId) {
            setSelectedStateId(location.id);
        } else if (!selectedCityId) {
            setSelectedCityId(location.id);
        } else {
            setSelectedBranchId(location.id);
        }
        setBreadcrumb(prev => [...prev, location.name]);
    };

    const handleBreadcrumbClick = (index) => {
        // Keep a snapshot of the current selections *before* we start mutating them
        const prevZone = selectedZone;
        const prevStateId = selectedStateId;
        const prevCityId = selectedCityId;

        // Trim the breadcrumb to the clicked index
        const newBreadcrumb = breadcrumb.slice(0, index + 1);
        setBreadcrumb(newBreadcrumb);

        /* ------------------------------------------------------------------
         *  Handle the fixed, non-location crumbs first
         * ----------------------------------------------------------------*/
        if (index === 0) {                                  //  All Clients
            setSelectedAccountId(null);
            setSelectedJobroleId(null);
            setSelectedRolesForDropdown([]);
            // Clear *all* location filters
            setSelectedZone(null);
            setSelectedStateId(null);
            setSelectedCityId(null);
            setSelectedBranchId(null);
            return;
        }

        // Client crumb – only if a client is actually selected
        if (index === 1 && selectedAccountId) {
            const clientName = newBreadcrumb[1];
            const client = accountsData?.allUserAccount?.find(a => a.name === clientName);
            if (client) {
                setSelectedAccountId(client.id);
            }
            setSelectedJobroleId(null);                     //  Any role filter is now invalid
            // Clear deeper location filters
            setSelectedZone(null);
            setSelectedStateId(null);
            setSelectedCityId(null);
            setSelectedBranchId(null);
            return;
        }

        // --- Role crumb (only if role is part of the trail) ---
        if (selectedJobroleId && index === 2) {
            const clientName = newBreadcrumb[1];
            const roleName = newBreadcrumb[2];
            const client = accountsData?.allUserAccount?.find(a => a.name === clientName);
            if (client) {
                setSelectedAccountId(client.id);
                const role = selectedRoles[client.id]?.find(r => r.name === roleName);
                setSelectedJobroleId(role?.id || null);
            }
            // Clear location filters completely
            setSelectedZone(null);
            setSelectedStateId(null);
            setSelectedCityId(null);
            setSelectedBranchId(null);
            return;
        }

        /* ------------------------------------------------------------------
         *  We are now dealing with LOCATION crumbs (Zone → State → City)
         * ----------------------------------------------------------------*/
        // Determine how many fixed (non-location) crumbs precede location crumbs
        const filterCount = (selectedAccountId ? 1 : 0) + (selectedJobroleId ? 1 : 0) + 1; // +1 for "All Clients"
        const locationStartIndex = filterCount;                     // First location crumb index
        const clickedLevel = index - locationStartIndex + 1;        // 1 = Zone, 2 = State, 3 = City

        // If we clicked before locationStartIndex, nothing more to do
        if (clickedLevel < 1) return;

        // Reset *deeper* levels only ----------------------------------------------------
        if (clickedLevel < 3) setSelectedCityId(null);   //  Clicking Zone or State removes City
        if (clickedLevel < 2) setSelectedStateId(null); //  Clicking Zone removes State
        setSelectedBranchId(null);                      //  Branch always cleared on breadcrumb click

        // Apply the correct selections based on the breadcrumb clicked ------------------
        switch (clickedLevel) {
            case 1:   // Zone level
                // Restore the zone name from the breadcrumb itself
                setSelectedZone(newBreadcrumb[index]);
                break;

            case 2:   // State level
                setSelectedZone(prevZone);             //  Zone remains the same
                setSelectedStateId(prevStateId);       //  Restore the *known* state ID
                break;

            case 3:   // City level (should never be clickable per UI, but handled for safety)
                setSelectedZone(prevZone);
                setSelectedStateId(prevStateId);
                setSelectedCityId(prevCityId);
                break;
            default:
                break;
        }
    };

    const [exportCSV, { loading: exportLoading }] = useLazyQuery(GET_EXPORT_ACCOUNT_SUMMARY, {
        fetchPolicy: "network-only",
        onCompleted: (data) => {
            if (data?.getExportAccountSummary) {
                // Trigger file download
                const blob = new Blob([data.getExportAccountSummary], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;

                // Dynamic filename
                const clientName = selectedAccountId
                    ? accountsData?.allUserAccount?.find(a => a.id === selectedAccountId)?.name || 'Client'
                    : 'All_Clients';
                const roleName = selectedJobroleId
                    ? selectedRoles[selectedAccountId]?.find(r => r.id === selectedJobroleId)?.name || 'Role'
                    : 'All_Roles';
                const timestamp = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

                link.setAttribute('download', `${clientName}_${roleName}_Report_${timestamp}.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);

                message.success("CSV exported successfully!");
            }
        },
        onError: (error) => {
            console.error("Export failed:", error);
            message.error("Failed to export CSV. Please try again.");
        }
    });

    const handleExport = () => {
        if (exportLoading) return;

        exportCSV({
            variables: {
                accountId: selectedAccountId || null,
                jobroleId: selectedJobroleId || null,
            },
        });
    };

    // AUTO-DRILL INTO FIRST ZONE when Client + Role is selected
    useEffect(() => {
        if (true) return; // auto-drill disabled; wait for explicit row click

        if (!selectedAccountId || !selectedJobroleId || summaryLoading || !summaryData?.getLocationSummary?.data) return;

        const locations = summaryData.getLocationSummary.data;

        // Only auto-drill if we're at the top level (i.e., showing zones/regions)
        const isAtTopLevel = breadcrumb.length === 3 && breadcrumb[0] === 'All Clients'; // After Client + Role selected

        if (isAtTopLevel && locations.length > 0) {
            const firstLocation = locations[0];

            // Only proceed if not already drilled into this zone
            if (breadcrumb[breadcrumb.length - 1] !== firstLocation.name) {
                setSelectedZone(firstLocation.name);
                setBreadcrumb(prev => [...prev, firstLocation.name]);
            }
        }
    }, [selectedAccountId, selectedJobroleId, summaryData, summaryLoading, breadcrumb]);

    const currentData = useMemo(() => {
        if (summaryLoading || !summaryData?.getLocationSummary?.data) return [];

        const filterCount = selectedAccountId && selectedJobroleId ? 3 : selectedAccountId ? 2 : 1;
        const currentDrillLevel = breadcrumb.length - filterCount;

        return summaryData.getLocationSummary.data.map(item => ({
            id: item.id || 0,
            name: item.state_name || item.city_name || item.name,
            dartsUsers: item.darts_users || 0,
            clanUsers: item.clan_users || 0,
            activeToday: item.active_today || 0,
            activeMTD: item.active_mtd || 0,
            totalUsers: item.total_users || 0,
            adoption: item.adoption_rate ?? 0,
            latitute: item.latitute,     // ← Real from API
            longitude: item.longitude,
            // Use server flag when available; fallback to drill-level heuristic
            hasChildren: item.hasChildren !== undefined ? item.hasChildren : currentDrillLevel < 3
        }));
    }, [summaryData, summaryLoading, breadcrumb.length, selectedAccountId, selectedJobroleId]);

    const { loading, error, data } = useQuery(Dashboard_API);
    const navigate = useNavigate();
    const [selectedRoles, setSelectedRoles] = useState({});
    const [selectedValues, setSelectedValues] = useState({});
    const [loadingRoles, setLoadingRoles] = useState({});
    const [imageError, setImageError] = useState({});
    const [searchQuery, setSearchQuery] = useState("");

    const { loading: accountsLoading, error: accountsError, data: accountsData } = useQuery(Accounts_Query);

    const [fetchRoles] = useLazyQuery(Role_Account_Query, {
        fetchPolicy: 'network-only',
        onCompleted: (data, context) => {
            const roles = data?.getAllJobrole || [];
            let accountId = roles[0]?.account?.id;
            if (!accountId && context?.variables) {
                accountId = context.variables.accountId;
            }
            if (accountId) {
                setSelectedRoles((prev) => ({ ...prev, [accountId]: roles }));
                setLoadingRoles((prev) => ({ ...prev, [accountId]: false }));
            }
        },
        onError: (error, context) => {
            const accountId = context?.variables?.accountId;
            if (accountId) {
                setSelectedRoles((prev) => ({ ...prev, [accountId]: [] }));
                setLoadingRoles((prev) => ({ ...prev, [accountId]: false }));
            }
        },
    });

    const handleImageError = (userId) => {
        setImageError((prev) => ({ ...prev, [userId]: true }));
    };

    const handleAccountChange = (accountId) => {
        if (!selectedRoles.hasOwnProperty(accountId)) {
            setLoadingRoles((prev) => ({ ...prev, [accountId]: true }));
            fetchRoles({ variables: { accountId: parseInt(accountId) } });
        }
    };

    const handleFilterAccountChange = (accountId) => {
        if (selectedRoles[accountId]) {
            setSelectedRolesForDropdown(selectedRoles[accountId]);
            return;
        }

        setLoadingRoles(prev => ({ ...prev, [accountId]: true }));
        fetchRoles({
            variables: { accountId: parseInt(accountId) },
            onCompleted: (data) => {
                const rolesList = data?.getAllJobrole || [];
                setSelectedRoles(prev => ({ ...prev, [accountId]: rolesList }));
                setSelectedRolesForDropdown(rolesList);
                setLoadingRoles(prev => ({ ...prev, [accountId]: false }));
            },
            onError: () => {
                setLoadingRoles(prev => ({ ...prev, [accountId]: false }));
            }
        });
    };

    const handleDropdownRoleChange = (value, accountId) => {
        setSelectedValues((prev) => ({
            ...prev,
            [accountId]: value,
        }));
    };

    if (accountsLoading)

        if (accountsLoading) return (
            <div className="loading-container">
                <div className="spinner"></div>
            </div>
        );

    if (accountsError) return <div>Error: {accountsError.message}</div>;

    const filteredAccounts = accountsData?.allUserAccount
        ?.filter((user) => user.name.toLowerCase().includes(searchQuery.toLowerCase()))
        ?.reverse();

    const statsData = {
        clients: data?.getDashboardValues?.totalClients,
        roles: data?.getDashboardValues?.totalRoles,
        users: data?.getDashboardValues?.totalUsers,
    };

    const overviewData = [
        { name: "LENSE", image: Lense, hoverImage: LenseLogo, totalUsers: data?.getDashboardValues?.totalLenseUsers },
        { name: "DARTS", image: Darts, hoverImage: DartsLogo, totalUsers: data?.getDashboardValues?.totalDartsUsers },
        { name: "CLAN", image: Clan, hoverImage: ClanLogo, totalUsers: data?.getDashboardValues?.totalClanUsers },
    ];

    return (
        <Content>
            <div className="dashboard-container">
                <div className="dashboard-content">
                    <div className="max-w-8xl mx-auto mb-2">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="flex items-center justify-between py-1 bg-white border-gray-200">
                                    {/* Left: Dashboard Title */}
                                    <h1 className="text-2xl font-bold text-gray-900">
                                        {selectedClient ? `${selectedClient.name} Dashboard` : "Admin Dashboard"}
                                    </h1>

                                    {/* Right: Filters + Export CSV */}
                                    <div className="flex items-center gap-4">
                                        {/* 1. Client Dropdown */}
                                        <Select
                                            showSearch
                                            allowClear
                                            placeholder="Select a client"
                                            style={{ width: 200 }}
                                            size="large"
                                            value={selectedAccountId}
                                            onChange={(value) => {
                                                setSelectedAccountId(value ?? null);
                                                setSelectedJobroleId(null);
                                                setSelectedZone(null);
                                                setSelectedStateId(null);
                                                setSelectedCityId(null);
                                                setSelectedRolesForDropdown([]);

                                                if (value) {
                                                    const client = accountsData?.allUserAccount?.find(a => a.id === value);
                                                    setBreadcrumb(['All Clients', client?.name || 'Client']);
                                                    // Load roles for this client
                                                    handleFilterAccountChange(value);
                                                } else {
                                                    setBreadcrumb(['All Clients']);
                                                }
                                            }}
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                (option?.children?.props?.children?.[0] || '').toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            <Select.Option value={null}>All Clients</Select.Option>
                                            {accountsData?.allUserAccount?.map((account) => (
                                                <Select.Option key={account.id} value={account.id}>
                                                    <div className="flex justify-between">
                                                        <span>{account.name}</span>
                                                        {/* <span className="text-gray-400 text-xs">ID: {account.id}</span> */}
                                                    </div>
                                                </Select.Option>
                                            ))}
                                        </Select>

                                        {/* 2. Role Dropdown - DEPENDENT */}
                                        <Select
                                            showSearch
                                            allowClear
                                            placeholder={selectedAccountId ? "Select a role" : "First select a client"}
                                            style={{ width: 200 }}
                                            size="large"
                                            value={selectedJobroleId}
                                            onChange={(value) => {
                                                setSelectedJobroleId(value ?? null);
                                                setSelectedZone(null);
                                                setSelectedStateId(null);
                                                setSelectedCityId(null);

                                                if (value && selectedAccountId) {
                                                    const role = selectedRoles[selectedAccountId]?.find(r => r.id === value);
                                                    const client = accountsData?.allUserAccount?.find(a => a.id === selectedAccountId);

                                                    // Set breadcrumb: All Clients → ClientName → RoleName
                                                    setBreadcrumb(['All Clients', client?.name || 'Client', role?.name || 'Role']);
                                                } else if (!value && selectedAccountId) {
                                                    const client = accountsData?.allUserAccount?.find(a => a.id === selectedAccountId);
                                                    setBreadcrumb(['All Clients', client?.name || 'Client']);
                                                }
                                            }}
                                            disabled={!selectedAccountId}
                                            loading={loadingRoles[selectedAccountId]}
                                            options={
                                                selectedRoles[selectedAccountId]?.map(role => ({
                                                    value: role.id,
                                                    label: role.name,
                                                })) || []
                                            }
                                        />

                                        <button
                                            onClick={handleExport}
                                            disabled={exportLoading}
                                            className="flex items-center gap-2 px-4 py-3 bg-[#AD0101] text-white rounded-md hover:bg-[#b00000] transition-colors whitespace-nowrap disabled:opacity-70"
                                        >
                                            {exportLoading ? (
                                                <>
                                                    <Spin size="small" /> Exporting...
                                                </>
                                            ) : (
                                                "Export CSV"
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <div className="stats-container" >
                                    <div className="stats-left">
                                        <div className="stats-item-client">
                                            <div className="stats-icon">
                                                <img className="super-manager-image-client" src={Clients} alt="Clients" />
                                            </div>
                                            <div className="stats-content">
                                                <span className="stats-client">{statsData.clients === 1 ? "Client" : "Clients"}</span>
                                                <span className="stats-value-client">{statsData.clients}</span>
                                            </div>
                                        </div>

                                        <div className="stats-item">
                                            <div className="stats-icon">
                                                <img className="super-manager-image" src={Roles} alt="Roles" />
                                            </div>
                                            <div className="stats-content">
                                                <span className="stats-label">{statsData.roles === 1 ? "Role" : "Roles"}</span>
                                                <span className="stats-value">{statsData.roles}</span>
                                            </div>
                                        </div>

                                        <div className="stats-item">
                                            <div className="stats-icon">
                                                <img className="super-manager-image" src={Users} alt="Users" />
                                            </div>
                                            <div className="stats-content">
                                                <span className="stats-label">{statsData.users === 1 ? "Product User" : "Product Users"}</span>
                                                <span className="stats-value">{statsData.users}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="stats-right">
                                        {overviewData.map((item, index) => (
                                            <div
                                                className={`product-card-dashboard ${hoverIndex === index ? "hovered" : ""}`}
                                                key={index}
                                                onMouseEnter={() => setHoverIndex(index)}
                                                onMouseLeave={() => setHoverIndex(null)}
                                            >
                                                <div className="product-logo-super-manager">
                                                    <img src={hoverIndex === index && item.hoverImage ? item.hoverImage : item.image} alt={item.name} />
                                                </div>
                                                <div className="product-stats">
                                                    <span className="product-label">Total Users</span>
                                                    <span className="product-value">{item.totalUsers}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Top Metrics */}
                    <div className=" mx-auto mb-2">
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                            {/* ... (your existing metric cards) ... */}
                            <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-gray-600">Active Today</span>
                                </div>
                                <div className="text-2xl font-bold text-gray-900">{topMetrics.activeToday.toLocaleString()}</div>
                                <div className="text-xs text-gray-500 mt-1">Users</div>
                            </div>
                            <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-gray-600">Active MTD</span>
                                </div>
                                <div className="text-2xl font-bold text-gray-900">{topMetrics.activeMTD.toLocaleString()}</div>
                                <div className="text-xs text-gray-500 mt-1">This Month</div>
                            </div>
                            <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-gray-600">New This Month</span>
                                </div>
                                <div className="text-2xl font-bold text-gray-900">{topMetrics.newThisMonth.toLocaleString()}</div>
                                <div className="text-xs text-gray-500 mt-1">Users</div>
                            </div>
                            <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-gray-600">Adoption Rate</span>
                                </div>
                                <div className="text-2xl font-bold text-gray-900">{topMetrics.adoptionRate}</div>
                                <div className="text-xs text-gray-500 mt-1">Active/Total</div>
                            </div>
                        </div>

                        {/* Location Split Section */}
                        <div className="bg-white rounded-lg border border-gray-200">
                            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900">Location Split</h2>
                                    <div className="flex items-center gap-2 mt-2">
                                        {breadcrumb.map((crumb, index) => (
                                            <React.Fragment key={index}>
                                                {index > 0 && <ChevronRight size={14} className="text-gray-400" />}
                                                {index === breadcrumb.length - 1 ? (
                                                    // LAST ITEM → Black & NOT clickable
                                                    <span className="text-sm text-gray-900 font-medium">
                                                        {crumb}
                                                    </span>
                                                ) : (
                                                    // PREVIOUS ITEMS → Red & Clickable
                                                    <button
                                                        onClick={() => handleBreadcrumbClick(index)}
                                                        className="text-sm text-[#AD0101] hover:text-[#b00000] font-medium transition-colors"
                                                    >
                                                        {crumb}
                                                    </button>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setViewMode('table')}
                                        className={`p-2 rounded-lg transition-colors ${viewMode === 'table' ? 'bg-[#AD0101] text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                                    >
                                        <List size={20} />
                                    </button>
                                    <button
                                        onClick={() => setViewMode('map')}
                                        className={`p-2 rounded-lg transition-colors ${viewMode === 'map' ? 'bg-[#AD0101] text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                                    >
                                        <Map size={20} />
                                    </button>
                                </div>
                            </div>

                            {viewMode === 'table' ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50 border-b border-gray-200">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Darts Users</th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Clan Users</th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Active Today</th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Active MTD</th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Adoption %</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {summaryLoading ? (
                                                <tr><td colSpan="7" className="text-center py-12"><div className="loading-container">
                                                    <div className="spinner"></div>
                                                </div></td></tr>
                                            ) : currentData.length === 0 ? (
                                                <tr><td colSpan="7" className="text-center py-12 text-gray-500">No data</td></tr>
                                            ) : (
                                                currentData.map((location) => (
                                                    <tr
                                                        key={location.id}
                                                        className="hover:bg-gray-50 transition-colors"
                                                        style={{ cursor: location.hasChildren ? 'pointer' : 'default' }}
                                                        onClick={() => {
                                                            if (!location.hasChildren) return;

                                                            // Count how many breadcrumb items are "filters" (All Clients + Client + Role)
                                                            const filterCount = selectedAccountId && selectedJobroleId
                                                                ? 3
                                                                : selectedAccountId
                                                                    ? 2
                                                                    : 1;

                                                            const currentDrillLevel = breadcrumb.length - filterCount;

                                                            // Now drill down correctly at every level
                                                            if (currentDrillLevel === 0) {
                                                                // First click after filters → Zone
                                                                setSelectedZone(location.name);
                                                            }
                                                            else if (currentDrillLevel === 1) {
                                                                // Second click → State
                                                                setSelectedStateId(location.id);
                                                            }
                                                            else if (currentDrillLevel === 2) {
                                                                // Third click → City
                                                                setSelectedCityId(location.id);
                                                            }
                                                            else if (currentDrillLevel === 3) {
                                                                // Fourth click → Branch (optional)
                                                                setSelectedBranchId(location.id);
                                                            }

                                                            // Add to breadcrumb if not already there
                                                            if (breadcrumb[breadcrumb.length - 1] !== location.name) {
                                                                setBreadcrumb(prev => [...prev, location.name]);
                                                            }
                                                        }}
                                                    >
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center gap-2">
                                                                {location.name === 'Unmapped' && <MapPin size={16} className="text-gray-400" />}
                                                                <span className="text-sm font-medium text-gray-900">{location.name}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">{location.dartsUsers.toLocaleString()}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">{location.clanUsers.toLocaleString()}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">{location.activeToday.toLocaleString()}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">{location.activeMTD.toLocaleString()}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full">
                                                                {location.adoption > 0 ? `${location.adoption}%` : '—'}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                                            {false && <ChevronRight size={18} className="text-gray-400 inline" />}  {/* Disabled for clients */}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                        {/* Footer total row - unchanged */}
                                        <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                                            <tr>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">Total</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-bold text-gray-900">
                                                    {currentData.reduce((s, l) => s + l.dartsUsers, 0).toLocaleString()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-bold text-gray-900">
                                                    {currentData.reduce((s, l) => s + l.clanUsers, 0).toLocaleString()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-bold text-gray-900">
                                                    {currentData.reduce((s, l) => s + l.activeToday, 0).toLocaleString()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-bold text-gray-900">
                                                    {currentData.reduce((s, l) => s + l.activeMTD, 0).toLocaleString()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-bold text-gray-900">
                                                    {currentData.length > 0 ? (
                                                        (currentData.reduce((s, l) => s + l.adoption * (l.dartsUsers + l.clanUsers), 0) /
                                                            currentData.reduce((s, l) => s + l.dartsUsers + l.clanUsers, 0)
                                                        ).toFixed(1) + '%'
                                                    ) : '—'}
                                                </td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            ) : (
                                <div className="p-6">
                                    <div style={{ height: '400px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                        <MapContainer
                                            center={[20.5937, 78.9629]}
                                            zoom={5}
                                            style={{ height: '100%', width: '100%' }}
                                            scrollWheelZoom={true}
                                        >
                                            <TileLayer
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors'
                                            />

                                            {/* Real markers from API lat/long */}
                                            {currentData
                                                .filter(loc => loc.latitute && loc.longitude && loc.name !== 'Unmapped')
                                                .map((location) => {
                                                    const lat = parseFloat(location.latitute);
                                                    const lng = parseFloat(location.longitude);
                                                    if (isNaN(lat) || isNaN(lng)) return null;

                                                    const totalUsers = location.dartsUsers + location.clanUsers;
                                                    const radius = Math.max(20, Math.sqrt(totalUsers) * 3);

                                                    return (
                                                        <CircleMarker
                                                            key={`${location.id}-${location.name}`}
                                                            center={[lat, lng]}
                                                            radius={radius}
                                                            fillColor="#AD0101"
                                                            color="#B00000"
                                                            weight={2}
                                                            opacity={1}
                                                            fillOpacity={0.75}
                                                            eventHandlers={{
                                                                click: () => handleDrillDown(location)
                                                            }}
                                                        >
                                                            <Tooltip direction="top" offset={[0, -10]} opacity={1}>
                                                                <div style={{ padding: '8px', background: 'white', borderRadius: '6px', boxShadow: '0 2px 10px rgba(0,0,0,0.2)' }}>
                                                                    <div style={{ fontWeight: 'bold', fontSize: '15px', color: '#AD0101' }}>{location.name}</div>
                                                                    <div>Darts: <strong>{location.dartsUsers}</strong></div>
                                                                    <div>Clan: <strong>{location.clanUsers}</strong></div>
                                                                    <div>Active Today: <strong>{location.activeToday}</strong></div>
                                                                    <div>Active MTD: <strong>{location.activeMTD}</strong></div>
                                                                    <div>Adoption: <strong>{location.adoption > 0 ? `${location.adoption.toFixed(1)}%` : '—'}</strong></div>
                                                                </div>
                                                            </Tooltip>
                                                        </CircleMarker>
                                                    );
                                                })}

                                            {/* Auto-fit map to markers - using useMap hook */}
                                            <FitBoundsHook data={currentData} />
                                        </MapContainer>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Clients Section */}
                    <div className="mt-8">
                        <h2 className="dashboardHeading">Clients</h2>
                    </div>
                    <Content>
                        <div className="cards-container-dashboard">
                            {filteredAccounts?.slice(0, 4).map((user) => {
                                const isRoleSelected = Boolean(selectedValues[user.id]);
                                const hasNoRoles = user.numberOfRoles === 0;
                                return (
                                    <div key={user.id} className="card">
                                        <div className="card-header">
                                            {imageError[user.id] || !user.mainLogoUrl ? (
                                                <div className="placeholderImage">
                                                    <span className="placeholder-text">No Image</span>
                                                </div>
                                            ) : (
                                                <img
                                                    src={user.mainLogoUrl}
                                                    alt={user.name}
                                                    className="card-image"
                                                    onError={() => handleImageError(user.id)}
                                                />
                                            )}
                                        </div>
                                        <h3 className="card-title">{user.name}</h3>
                                        <div className="card-actions">
                                            <SearchableDropdown
                                                options={selectedRoles[user.id] || []}
                                                label="name"
                                                id="id"
                                                selectedVal={selectedValues[user.id]}
                                                handleChange={(value) => handleDropdownRoleChange(value, user.id)}
                                                placeholder={
                                                    hasNoRoles && !loadingRoles[user.id]
                                                        ? "No roles found"
                                                        : loadingRoles[user.id]
                                                            ? "Loading roles..."
                                                            : "Select Role"
                                                }
                                                onFocus={() => handleAccountChange(user.id)}
                                                disabled={false}
                                                loading={loadingRoles[user.id]}
                                                notFoundContent={hasNoRoles && !loadingRoles[user.id] ? "No roles found" : loadingRoles[user.id] ? <div className="loading-container">
                                                    <div className="spinner"></div>
                                                </div> : null}
                                            />
                                            <button
                                                className="view-role-button"
                                                onClick={() => {
                                                    const selectedRoleId = selectedValues[user.id];
                                                    if (selectedRoleId) {
                                                        const selectedRoleData = selectedRoles[user.id]?.find(role => role.id === selectedRoleId);
                                                        if (selectedRoleData) {
                                                            const cleanedColor = (user.brandColor || "").replace(/^#/, "");
                                                            navigate("/admin-view-role", {
                                                                state: {
                                                                    roleId: selectedRoleId,
                                                                    accountId: selectedRoleData.account.id,
                                                                    accountName: selectedRoleData.account?.name || "Unknown",
                                                                    name: selectedRoleData.name,
                                                                    dartsNeeded: selectedRoleData.dartsNeeded,
                                                                    clanNeeded: selectedRoleData.clanNeeded,
                                                                    syncNeeded: selectedRoleData.syncNeeded || false,
                                                                    lenseNeeded: selectedRoleData.lenseNeeded || false,
                                                                    emailLogin: selectedRoleData.emailLogin,
                                                                    inputEditDays: selectedRoleData.inputEditDays,
                                                                    mandatoryTimeDistribution: selectedRoleData.mandatoryTimeDistribution,
                                                                    numberLogin: selectedRoleData.numberLogin,
                                                                    pocEmail: selectedRoleData.pocEmail || "",
                                                                    pocName: selectedRoleData.pocName || "",
                                                                    pocNumber: selectedRoleData.pocContactNumber || "",
                                                                    targetEditDays: selectedRoleData.targetEditDays,
                                                                    dartsFormNeeded: selectedRoleData.dartsFormNeeded,
                                                                    clientName: user.name,
                                                                    clientUrl: user.url || "",
                                                                    clientAddress: user.address || "",
                                                                    clientAddress2: user.address2 || "",
                                                                    clientBrandColor: cleanedColor || "C12328",
                                                                },
                                                            });
                                                        }
                                                    }
                                                }}
                                                disabled={!selectedValues[user.id] || hasNoRoles}
                                                style={{
                                                    backgroundColor: selectedValues[user.id] ? "#C12328" : hasNoRoles ? "#dcdcdc" : "#FAC9CA",
                                                    cursor: selectedValues[user.id] ? "pointer" : "not-allowed",
                                                }}
                                            >
                                                View Role
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {filteredAccounts?.length > 3 && (
                            <div className="more-clients-container">
                                <button className="more-clients-btn" onClick={() => navigate("/admin-home")}>
                                    More Clients
                                </button>
                            </div>
                        )}
                    </Content>
                </div>
            </div>
        </Content >
    );
};

export default AdminDashboard;